<i18n>
{
  "de": {
    "addComment": "Kommentieren",
    "nothingToSee": "Hier gibt es bisher noch nichts zu sehen...",
    "whatWouldShare": "Was möchtest du teilen?",
    "pinnedCommentsHeading": "Hervorgehobene Beiträge",
    "countComments": "{count} Kommentar | {count} Kommentare",
    "hideComments": "Kommentare ausblenden",
    "showComments": "Kommentare anzeigen",
    "showAllComments": "Alle Kommentare anzeigen",
    "leaveComment": "Schreibe den ersten Kommentar!"
  },
  "en": {
    "addComment": "Add Comment",
    "nothingToSee": "Nothing to see here yet...",
    "whatWouldShare": "What would you like to share?",
    "pinnedCommentsHeading": "Highlighted comments",
    "countComments": "{count} comment | {count} comments",
    "hideComments": "Hide comments",
    "showComments": "Show comments",
    "showAllComments": "Show all comments",
    "leaveComment": "Write the first comment!"
  }
}
</i18n>

<template>
  <div
    v-if="!post.locked && post.commentsEnabled"
    class="post-comments-container"
  >
    <div class="post-comments-control">
      <div
        v-if="!isolatedCommentView"
        :id="commentsToggleId"
        class="comments-toggle"
      >
        <div v-if="!initialized">
          <button
            v-if="post.commentsCount > 0"
            class="button show-comments-button is-outline is-small on-tile"
            @click.prevent="initComments"
          >
            {{ $tc('countComments', commentsCount, { count: commentsCount }) }}
          </button>
          <button
            v-else
            class="button show-comments-button is-small on-tile"
            @click.prevent="initComments"
          >
            {{ $t('addComment') }}
          </button>
        </div>
        <div v-else>
          <button
            class="button show-comments-button is-outline is-small on-tile"
            @click.prevent="toggleComments"
          >
            <span v-if="hidden">{{ $t('showComments') }}</span>
            <span v-else>{{ $t('hideComments') }}</span>
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="!hidden || isolatedCommentView"
      @scroll="handleScroll"
    >
      <cta-comment
        v-if="!isAuthenticated"
        class="unauthorized"
      />
      <editor
        v-if="editorVisible && isAuthenticated"
        :editorId="rootEditorId"
        :options="editorOptions"
        :targetComment="null"
        class="on-tiles-background"
        @editormessage="handleEditorMessage"
      />

      <div
        v-if="loading && !isolatedCommentView"
        class="mt-25 is-flex is-justify-content-center"
      >
        <hollow-dots-spinner
          id="hollow-dots"
          :animation-duration="1000"
          :dot-size="10"
          :dots-num="3"
          color="#fb3e52"
        />
      </div>
      <div v-if="isolatedCommentView">
        <div class="mt-5 mb-5">
          <nuxt-link
            :to="localePath({
              name: 'id-post-slug-postId',
              params: {
                id: artist.urlAlias,
                postId: post._id,
                slug: urlSlug(post.title),
                title: `${artist.nickname} - ${post.title}`
              },
              // query: { comments: 'init', refresh: 'true' }
              query: { comments: 'init' }
            })"
            data-qa="post-link"
          >
            {{ $t('showAllComments') }}
          </nuxt-link>
        </div>
        <div class="comments-container">
          <post-comment
            v-if="commentData.parent"
            :key="commentData.parent._id"
            :user="artist"
            :comment="commentData.parent"
            :index="0"
            :community="false"
            :level="0"
            :displayContext="'isolatedView'"
          />
          <post-comment
            :key="commentData.comment._id"
            :user="artist"
            :comment="commentData.comment"
            :index="commentData.parent ? 1 : 0"
            :community="false"
            :level="commentData.parent ? 1 : 0"
            :displayContext="'isolatedView'"
          />
        </div>
      </div>
      <div v-else-if="initialized">
        <div class="comments-container">
          <div
            v-if="!allComments.length"
            class="box mt-25"
          >
            {{ $t('nothingToSee') }}
            <span>{{ $t('leaveComment') }}</span>
          </div>

          <div
            v-if="pinnedComments.length"
            class="pinned-comments mt-25 mb-25"
          >
            <h3 class="ml-3">
              {{ $t('pinnedCommentsHeading') }}
            </h3>
            <post-comment
              v-for="(comment, index) in pinnedComments"
              :key="generateKey(`${index}_${comment._id}`)"
              :user="artist"
              :comment="comment"
              :index="index"
              :community="true"
              :pinned="true"
              :level="0"
              :displayContext="displayContext"
              @deletecomment="deleteComment"
              @pinned="handlePinned"
            />
            <hr>
          </div>

          <post-comment
            v-for="(comment, index) in regularRootComments"
            :key="generateKey(`${index}_${comment._id}`)"
            :user="artist"
            :comment="comment"
            :index="index"
            :community="true"
            :level="0"
            :displayContext="displayContext"
            @pinned="handlePinned"
            @deletecomment="deleteComment"
          />
        </div>
      </div>
    </div>

    <portal
      v-if="activePortal"
      :to="`post_editor_${commentEditorTarget}`"
    >
      <editor
        v-if="isAuthenticated"
        :id="portableEditorId"
        :editorId="portableEditorId"
        :options="activeEditorOptions"
        :targetComment="null"
        class="on-comments-background"
        @editormessage="handleEditorMessage"
      />
    </portal>
  </div>
</template>
<script>
import { mapState, mapActions } from 'vuex'
import postComment from '~/components/post-comment'
import editor from '~/components/editor'
import ctaComment from '~/components/cta-comment'
import { HollowDotsSpinner } from 'epic-spinners/dist/lib/epic-spinners.min.js'

export default {
  name: 'post-comments',
  props: ['post', 'displayContext', 'instantlyOpenComments', 'commentData'],

  components: {
    editor,
    postComment,
    ctaComment,
    HollowDotsSpinner
  },

  data() {
    return {
      editorOptions: {
        placeholder: this.$t('whatWouldShare')
      },
      commentEditorTarget: null,
      targetComment: null,
      editorVisible: false,
      loading: true,
      initialized: false,
      hidden: true
    }
  },

  provide() {
    return {
      moveEditor: this.moveEditor,
      deleteComment: this.deleteComment
    }
  },
  computed: {
    rootEditorId() {
      return `rootEditor_${this.post.id}`
    },
    portableEditorId() {
      return `portableEditor_${this.post.id}`
    },
    commentsToggleId() {
      return this.instantlyOpenComments
        ? `commentsTarget_${this.post.id}`
        : `commentsToggle_${this.post.id}`
    },
    activePortal() {
      return this.commentEditorTarget
    },
    activeEditorOptions() {
      const placeholder = this.targetPost ? `${this.$t('tell')} ${this.targetPost.nickname} ${this.$t('whatThink')}` : this.$t('whatWouldShare')
      return {
        placeholder
      }
    },
    isolatedCommentView() {
      return this.commentData && this.commentData.comment
    },
    pinnedComments() {
      return (this.postComments[this.post._id] || []).filter(c => c.pinned)
    },
    regularRootComments() {
      return (this.postComments[this.post._id] || []).filter(c => !c.parent && !c.pinned)
    },
    allComments() {
      return this.postComments[this.post._id] || []
    },
    commentsCount() {
      return this.post.commentsCount
    },
    ...mapState(['isAuthenticated', 'artist', 'postComments'])
  },

  methods: {
    ...mapActions(['loadAllPostComments']),
    generateKey(id) {
      return `${this.displayContext}_${id}`
    },
    moveEditor: function (targetComment) {
      this.targetComment = targetComment
      this.commentEditorTarget = targetComment._id
      this.scrollToId(this.portableEditorId)
    },
    async initComments() {
      this.hidden = false
      this.loading = true

      await this.loadAllPostComments({
        postId: this.post._id,
        access: this.post.access
      })

      this.showEditor()
      this.initialized = true
      this.loading = false
    },
    toggleComments() {
      this.hidden = !this.hidden
    },
    showEditor() {
      this.editorVisible = true
    },
    async handleEditorMessage(data) {
      if (data.command === 'cancel') {
        this.commentEditorTarget = null
        this.targetComment = null
      } else if (data.command === 'save') {
        if (data.editorId === this.rootEditorId) {
          if (this.targetComment && this.targetComment._id === this.commentEditorTarget) {
            this.commentEditorTarget = null
            this.targetComment = null
          }
        }
        await this.save(data)
        this.commentEditorTarget = null
        this.targetComment = null
      }
    },
    handlePinned(data) {
      this.$store.dispatch(
        'setPostCommentPinned',
        { postId: this.post._id, enabled: data.enabled, commentId: data.comment._id }
      )
      this.$root.$emit('flash', {
        type: `toasts.community.pinned.${data.enabled ? 'enabled' : 'disabled'}`,
        state: 'info',
        flashConfig: {},
        payload: {}
      })
    },
    async deleteComment(comment) {
      const result = await this.apiPost(`/api/private/${this.artist.urlAlias}/post/comment/delete`, {
        commentId: comment._id
      })
      if (result.success) {
        // this.$store.dispatch('deletePostComment', comment)
      }
    },
    async save(data, error) {
      if (!this.isAuthenticated) {
        console.log('Saving comment not allowed (not authenticated).')
        return false
      }
      data = {
        comment: {
          postId: this.post._id,
          parentId: this.activePortal,
          content: data.content
        }
      }

      try {
        const res = await this.apiPost(`/api/private/${this.artist.urlAlias}/post/comment/add`, data)
        if (res.comment) {
          if (!this.isolatedCommentView) {
            this.$store.dispatch('appendPostComments', {
              parent: this.targetComment || { ancestors: [] },
              comments: [res.comment]
            })
            this.scrollToId(`comment_${res.comment._id}`)
            return
          }

          const clonedCommentData = JSON.parse(JSON.stringify(this.commentData))
          clonedCommentData.comment.children = clonedCommentData.comment.children || []
          clonedCommentData.comment.children.push(res.comment)
          this.$store.dispatch('setSinglePost', {
            post: this.post,
            commentData: clonedCommentData
          })
        }
      } catch (e) {
        console.log('Comment could not be send (404)', e)
      }
    },
    scrollToId(id, prefix = '') {
      this.$nextTick(() => {
        setTimeout(() => {
          const target = document.getElementById(id)
          if (!target) return
          const container = target.closest('.comments-container')
          if (container) {
            const targetPosition = target.getBoundingClientRect().top - container.getBoundingClientRect().top
            container.scrollTo({
              top: targetPosition + container.scrollTop,
              behavior: 'smooth'
            })
          }
        }, 100)
      })
    },
    handleScroll(event) {
      event.stopPropagation()
    }
  },
  async mounted() {
    if (this.instantlyOpenComments) {
      await this.initComments()
      this.scrollToId(this.commentsToggleId)
    }
  },
  watch: {
    instantlyOpenComments(newVal) {
      if (newVal) {
        this.initComments().then(() => {
          this.scrollToId(this.commentsToggleId)
        })
      }
    }
  }
}
</script>

<style lang="scss">
.post-comments-container {
  display: flex;
  flex-direction: column;
}

.comments-toggle {
  text-align: center;
  margin: 10px 0;

  button {
    background-color: transparent;
  }
}

.comments-container {
  margin-top: 10px;
  width: 100%;
  max-height: 800px;
  overflow-y: auto;
  overflow-x: hidden;
  // Prevent background scroll
  -webkit-overflow-scrolling: touch;

  background-color: var(--comments-background-color) !important;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  .comment-meta {
    .has-text-primary, .comment-meta-date {
      color: var(--primary-on-comments-background) !important;
    }
  }

  .comment-content, .comment-meta, .editor {
    color: var(--comments-background-text-color) !important;
    .ql-stroke  {
      stroke: var(--comments-background-text-color) !important;
    }
  }

  .box {
    background-color: transparent !important;

    .dummy-avatar-icon, .quote-link {
      color: var(--primary-on-comments-background) !important;
    }
    .comment-controls {
      a {
        color: var(--primary-on-comments-background) !important;
      }
    }
  }
}

.editor {
  color: var(--tiles-background-text-color);
  background-color: transparent !important;

  .editor-form-controls {
    min-height: 42px;
  }
}
</style>
